import { WATCH_COMPONENT_IDS, type WatchRoom } from './watchSocket.constants';
import type { WatchAssetPayload, WatchInboundMessage, WatchPayload } from './watchSocket.types';
import { isComponentDataRoom, parseInboundMessage } from './watchSocketMessages';

/** One InfluxDB point as the cloud proxy relays it: ``{ measurement, tags, fields, timestamp }``. */
interface DataPoint {
  tags?: Record<string, unknown>;
  fields: WatchPayload;
  timestamp?: number;
}

const COMPONENT_ROOMS = [
  'components/input_data',
  'components/output_data',
  'components/container_data',
  'components/rtu_data',
  'components/plc_data',
  'components/bms_data',
] as const;

type ComponentRoom = (typeof COMPONENT_ROOMS)[number];

const isPlainObject = (value: unknown): value is WatchPayload =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isDataPoint = (value: unknown): value is DataPoint => isPlainObject(value) && isPlainObject(value.fields);

const isComponentRoom = (room: WatchRoom): room is ComponentRoom =>
  (COMPONENT_ROOMS as readonly string[]).includes(room);

const isMainInputPoint = ({ tags }: DataPoint): boolean =>
  tags?.component_id === undefined || tags.component_id === WATCH_COMPONENT_IDS.MAIN_INPUT;

const withTimestamp = ({ fields, timestamp }: DataPoint): WatchPayload => ({
  ...(typeof timestamp === 'number' ? { ts_measured: timestamp } : {}),
  ...fields,
});

const last = <T>(items: T[]): T => items[items.length - 1];

const groupByComponentId = (points: DataPoint[]): Record<string, DataPoint> =>
  Object.fromEntries(
    points.flatMap(({ tags, fields }) =>
      typeof tags?.component_id === 'string' ? [[tags.component_id, { tags, fields }]] : []
    )
  );

/**
 * Cloud counterpart of ``parseInboundMessage``: every data room arrives as a list of points
 * (``{ payload: [{ measurement, tags, fields, timestamp }] }``) instead of the per-room object the local
 * proxy relays. Each room is rewritten into the local shape its consumers already read. Anything that
 * is not a point list - feedback, config announcements, metadata - is left to the shared parser.
 */
export const parseCloudInboundMessage = (room: string, rawEnvelope: unknown): WatchInboundMessage | null => {
  const local = parseInboundMessage(room, rawEnvelope);

  if (!isPlainObject(rawEnvelope) || !('payload' in rawEnvelope)) {
    return local;
  }

  const payload = rawEnvelope.payload;
  const points = (Array.isArray(payload) ? payload : [payload]).filter(isDataPoint);

  if (points.length === 0) {
    return local;
  }

  switch (room) {
    case 'state':
      return { room, payload: { state: withTimestamp(last(points)) } };

    case 'timeseries':
      return { room, payload: { ts: withTimestamp(last(points)) } };

    case 'assets/state':
    case 'assets/timeseries': {
      // One point per asset: the consumers key them by asset id, so none may be dropped.
      const assets = points.map(
        (point): WatchAssetPayload => ({
          tags: (point.tags ?? {}) as WatchAssetPayload['tags'],
          fields: withTimestamp(point),
        })
      );

      return {
        room,
        payload: assets[assets.length - 1],
        assets: Object.fromEntries(
          assets
            .filter((item) => item.tags.asset_id !== undefined)
            .map((item) => [`${item.tags.asset_type}:${item.tags.asset_id}`, item])
        ),
      };
    }

    default: {
      if (!isComponentRoom(room as WatchRoom)) {
        return local;
      }

      const components = isComponentDataRoom(room as WatchRoom) ? groupByComponentId(points) : {};
      const hasComponents = Object.keys(components).length > 0;
      // input_data also carries the second inverter, which must not replace the main input's data.
      const candidates = room === 'components/input_data' ? points.filter(isMainInputPoint) : points;

      if (candidates.length === 0 && !hasComponents) {
        return null;
      }

      return {
        room,
        payload: {
          stream_identifier: room.split('/')[1],
          ...(candidates.length > 0 ? { fields: last(candidates).fields } : {}),
        },
        ...(hasComponents ? { components } : {}),
      } as WatchInboundMessage;
    }
  }
};
